<template>
  <div class="order-page" :class="{ 'no-operations': noOperations }">
    <Empty
      v-if="loaded && (!hasOrderPermission || order.status === 0)"
      text="您无权查看该订单"
    />
    <!----| SECTIONS |---->
    <template v-else-if="loaded">
      <!-- 顶部转态栏 -->
      <OrderStatus
        :order="order"
        :is-creator="false"
        :is-base-order="isBaseOrder"
        :refund-status="computedRefundStatus"
        @timeup="onTimeup"
      />
      <template v-if="isBaseOrder">
        <!-- 配送信息栏 status >= 30 -->
        <OrderDeliveryTile
          v-if="order.status >= 30 && order.status < 70"
          :order="order"
          :is-creator="false"
          :is-status-sent="isStatusSent"
          :refund-status="computedRefundStatus"
        />
        <HDivider :height="20" />
        <!-- 商品列表区 -->
        <OrderItems
          :order="order"
          :is-creator="false"
          :show-title="order.status >= 20 && order.status <= 60"
          @openImageViewer="openImageViewer"
        />
        <!-- 订单基础信息区 -->
        <OrderDetails :order="order" :refund-status="computedRefundStatus" />
        <!-- 订单支付信息区 -->
        <OrderPayDetail v-if="hasPayData" :data="order.orderPayLogVos" />
        <!-- 订单发票信息区 -->
        <OrderBillDetail v-if="hasBill" :data="order.billVo" />
        <!-- 配送方式表单 -->
        <OrderPageSection v-if="canChooseDelivery" is-panel>
          <OrderDeliveryForm
            ref="refOrderDeliveryForm"
            :order="order"
            @validate="onOrderDeliveryFormValidate"
          />
        </OrderPageSection>
      </template>
      <template v-else>
        <DepositOrderAmount :order="order" />
        <!-- 订单基础信息区 -->
        <DepositOrderDetails
          :order="order"
          :is-creator="false"
          :refund-status="computedRefundStatus"
        />
        <!-- 订单关联商品区 -->
        <DepositOrderItems
          :order="order"
          :is-creator="false"
          @openImageViewer="openImageViewer"
        />
        <!-- 订单支付信息区 -->
        <OrderPayDetail v-if="hasPayData" :data="order.orderPayLogVos" />
      </template>
      <!-- 支付条款 -->
      <OrderPageSection v-if="canPay">
        <div class="submit-privace-section">
          <van-checkbox
            v-model="paymentPrivacyChecked"
            shape="square"
            class="submit-privace-checkbox"
            >* 我已阅读并同意<span @click.stop="onClickPrivacyRules"
              >《隐私条款》</span
            >和<span @click.stop="onClickSalesRules">《销售条款》</span>
          </van-checkbox>
        </div>
      </OrderPageSection>
      <!-- 底部填充 -->
      <CBottomPad />
      <!-- 底部按钮区 -->
      <OrderPageButtons
        v-if="hasOperations"
        :order="order"
        :is-base-order="isBaseOrder"
        :can-pay="canPay"
        :can-split-pay="canSplitPay"
        :can-apply-bill="canApplyBill"
        :paymentPrivacyChecked="paymentPrivacyChecked"
        :deliveryFormValidated="deliveryFormValidated"
        :submiting="submiting"
        @paybyinstallments="payOrder"
        @payinfull="payOrder"
        @resetdelivery="chooseDelivery"
        @applybill="chooseInvoice"
        @showqrcode="onShowQrCode"
        @hideqrcode="onHideQrCode"
        @received="confirmReceived"
      />
    </template>
    <!----| POPUP |---->
    <!-- 通用确认框 -->
    <CConfirm ref="refCConfirm" />
    <!-- 图片查看器 -->
    <ImageViewer
      :visible="imageViewerVisible"
      :images="currentImages"
      :initial-index="currentImageIndex"
      :show-delete-btn="false"
      :show-upload-btn="false"
      @close="closeImageViewer"
      @change="onImageViewerChange"
    />
  </div>
</template>

<script>
  import {
    chooseDeliveryApi,
    createSelfPayUrl,
    getCustomerOrderDetail,
    receipt,
    setBill
  } from '@/api'
  import config from '@conf'
  import { chooseInvoiceTitle, sleep } from '@/utils'
  import orderPage from '@/mixins/order-page'
  import HDivider from '@/components/common/HDivider.vue'
  import OrderPageSection from '@/components/order/widgets/OrderPageSection.vue'
  import OrderDeliveryForm from '@/components/order/widgets/OrderDeliveryForm.vue'
  import OrderPageButtons from '@/components/order/sections/OrderPageButtonsForCustomer.vue'
  import DepositOrderAmount from '@/components/order/sections/DepositOrderAmount.vue'
  import ImageViewer from '@/components/common/ImageViewer.vue'
  import { getPublicOssUrl } from '@/domainMapping/index'

  import _mockOrder from '@/mock/data/order-detail'

  export default {
    mixins: [orderPage],
    components: {
      HDivider,
      OrderPageSection,
      OrderDeliveryForm,
      DepositOrderAmount,
      OrderPageButtons,
      ImageViewer
    },
    data() {
      return {
        paymentPrivacyChecked: false,
        deliveryFormValidated: false,
        currentOrderIndex: -1,
        currentImageIndex: 0,
        currentImages: [],
        imageViewerVisible: false
      }
    },
    computed: {
      canPay() {
        if (this.brandInfo.orderExpiry?.enabled) {
          const timingTo = this.$moment(
            this.order.submitTime || this.order.createdDate
          ).add(this.brandInfo.orderExpiry.expiryTime, 's')
          if (timingTo.isAfter(this.$moment())) {
            return this.order.status === 10
          }
          return false
        }
        return this.order.status === 10
      },
      canSplitPay() {
        return this.brandInfo.splitPayment?.enabled === true
      },
      canChooseDelivery() {
        return (
          !this.computedRefundStatus.isRefundingOrDone &&
          [10, 20].includes(this.order.status)
        )
      },
      canApplyBill() {
        return (
          this.brandInfo.fapiao?.enabled === true &&
          !this.computedRefundStatus.isRefundingOrDone &&
          this.isBaseOrder &&
          [20, 30, 40, 50, 60].includes(this.order.status) &&
          !this.hasBill
        )
      },
      canReceive() {
        return (
          !this.computedRefundStatus.isRefundingOrDone &&
          [40, 50].includes(this.order.status)
        )
      },
      hasOperations() {
        if (this.computedRefundStatus.isRefundingOrDone) return false
        return (
          // 隐含条件 this.order.status === 10
          this.canPay ||
          this.order.status === 20 ||
          // 隐含条件 this.order.status === 30, 40, 50, 60
          this.canApplyBill ||
          this.canReceive
        )
      },
      noOperations() {
        return !this.hasOperations
      }
    },
    created() {
      console.log('======created')
    },
    methods: {
      onClickPrivacyRules() {
        this.$router.push({
          name: 'PrivacyRules'
        })
      },
      onClickSalesRules() {
        this.$router.push({
          name: 'SalesRules'
        })
      },
      onOrderDeliveryFormValidate({ checked, error }) {
        console.log('onOrderDeliveryFormValidate', checked, error)
        this.deliveryFormValidated = checked
      },
      checkOrderIfFirstLoad() {
        if (!this.loaded) {
          this.loaded = true
          this.paymentPrivacyChecked = false
        }
      },
      /**
       * @param mode 'all' | 'billVo'
       */
      async updateOrderDetail(mode = 'all') {
        try {
          if (this.$route.query.orderNo) {
            const res = await getCustomerOrderDetail(this.$route.query.orderNo)
            if (mode === 'all') {
              res.orderItems.forEach(item => {
                const manualUploadImages = JSON.parse(item.manualUploadImages)
                if (!Array.isArray(manualUploadImages)) return
                item.manualUploadImages = manualUploadImages
                item.manualUploadImageShows = manualUploadImages.map(
                  filename => {
                    return getPublicOssUrl(filename)
                  }
                )
              })
              this.order = res
            }
            if (mode === 'billVo') {
              this.order.bill = res.bill
              this.order.billVo = res.billVo
            }
          } else if (config.isDevelopment) {
            await sleep(300)
            this.order = _mockOrder
          } else {
            throw new Error('缺少订单编号')
          }
          this.checkOrderIfFirstLoad()
        } catch (error) {
          return this.checkOrderPermission(error)
        }
      },
      async chooseDelivery(isReset) {
        if (this.$refs.refOrderDeliveryForm) {
          const { id: orderId, storeId } = this.order
          try {
            const deliveryData = this.$refs.refOrderDeliveryForm.getFormData()
            if (isReset) this.setSubmitting()
            await chooseDeliveryApi({
              ...deliveryData,
              orderId,
              storeId
            })
            await this.updateOrderDetail()
            if (isReset) this.unsetSubmitting()
            return true
          } catch (error) {
            console.log('chooseDeliveryApi error', error)
            this.unsetSubmitting(error?.message)
          }
          return false
        }
      },
      async payOrder() {
        if (!this.paymentPrivacyChecked) {
          this.$toast('请确认条款')
          return
        }
        this.setSubmitting()
        const { orderNo } = this.order
        if (this.isBaseOrder && !(await this.chooseDelivery(false))) {
          return
        }

        try {
          const payData = await createSelfPayUrl({
            orderNo
          })
          this.unsetSubmitting()
          if (payData?.payUrl) {
            location.href = payData.payUrl
          }
        } catch (error) {
          console.log('createSelfPayUrl error', error)
          this.unsetSubmitting(error?.message)
        }
      },
      async chooseInvoice() {
        try {
          const info = await chooseInvoiceTitle()
          this.setSubmitting()
          await setBill({
            orderId: this.order.id, //订单id
            bill: JSON.stringify({
              bankAccount: info.bankAccount,
              bankName: info.bankName,
              companyAddress: info.companyAddress,
              taxNumber: info.taxNumber,
              telephone: info.telephone,
              title: info.title,
              type: info.type
            })
          })
          await this.updateOrderDetail('billVo')
          this.unsetSubmitting('开票成功')
        } catch (error) {
          this.unsetSubmitting(error?.message)
        }
      },
      async confirmReceived() {
        if (await this.alert('确认收到货物?')) {
          try {
            this.setSubmitting()
            await receipt(this.order.id)
            await this.updateOrderDetail()
            this.unsetSubmitting('收货成功')
          } catch (error) {
            this.unsetSubmitting(error?.message)
          }
        }
      },
      onShowQrCode() {
        if (this._timer) clearInterval(this._timer)
        let isUpdating
        this._timer = setInterval(async () => {
          if (!isUpdating) {
            try {
              isUpdating = true
              await this.updateOrderDetail()
              isUpdating = false
            } catch (error) {
              if (error?.message) this.$toast(error.message)
            }
          }
        }, 2000)
      },
      async onHideQrCode() {
        if (this._timer) clearInterval(this._timer)
        this.onTimeup()
      },
      // 图片查看器相关方法
      openImageViewer({ orderIndex, imageIndex, images }) {
        this.currentOrderIndex = orderIndex
        this.currentImageIndex = imageIndex
        this.currentImages = images
        // 初始化item的图片索引
        const currentItem = this.order.orderItems[orderIndex]
        if (!currentItem.hasOwnProperty('manualUploadImagesIndex')) {
          this.$set(currentItem, 'manualUploadImagesIndex', imageIndex)
        }
        this.imageViewerVisible = true
      },
      closeImageViewer() {
        this.imageViewerVisible = false
        this.currentImages = []
        this.currentImageIndex = 0
        this.currentOrderIndex = -1
      },
      onImageViewerChange(index) {
        if (this.currentOrderIndex !== -1) {
          const currentItem = this.order.orderItems[this.currentOrderIndex]
          this.$set(currentItem, 'manualUploadImagesIndex', index)
        }
      }
    }
  }
</script>

<style lang="less" scoped>
  .order-page {
    width: 100%;
    height: 100%;
    padding-bottom: 120/20rem;
    overflow-x: hidden;
    overflow-y: auto;

    &.no-operations {
      padding-bottom: 50/20rem;
    }

    .submit-privace-section {
      margin-top: 20/20rem;
    }

    .submit-privace-checkbox {
      line-height: 17/20rem;
      font-weight: 400;
      font-size: 12/20rem;
      letter-spacing: 0;
      span {
        text-decoration: underline;
      }
    }
  }
</style>
